import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { dispatchRefreshTokenError } from '../utils/errorHandling';
import { useOrganizationsContext } from './OrganizationsContext';

interface EnterpriseData { enterprise: boolean }
interface SubscriptionData {
  subscription: {
    id: string;
    status: string;
    current_period_end: number;
    current_period_start?: number;
    cancel_at_period_end: boolean;
    trial_end?: number | null;
    trial_start?: number | null;
  } | null;
  customer_id: string | null;
  tier: string;
  plan_badge_name?: string;
  plan_badge_color?: string;
  pending_downgrade_price_id?: string;
  discount?: {
    promo_code?: string | null;
    promotion_code_id?: string | null;
    coupon_id?: string | null;
    coupon_name?: string | null;
    percent_off?: number | null;
    amount_off?: number | null;
    currency?: string | null;
    duration?: string | null;
    duration_in_months?: number | null;
    discount_end?: number | null;
  };
  limits?: {
    organizations?: number;
    apps_per_platform?: number;
    api_calls_per_month?: number;
    sandbox_environments?: number;
    active_rules?: number;
    active_policies?: number;
  };
}

interface SubscriptionContextValue {
  enterprise: boolean;
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  refetch: (silent?: boolean) => Promise<void>;
  optimisticUpdateSubscription: (updates: Partial<SubscriptionData>) => void;
  optimisticCancelSubscription: () => void;
  optimisticReactivateSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  // Get enterprise status from OrganizationsContext (already fetched in initial-data batch endpoint)
  // This avoids duplicate Auth0 requests
  const { enterprise: orgEnterprise, loading: orgsLoading } = useOrganizationsContext();
  const [enterprise, setEnterprise] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const fetchAll = useCallback(async (silent: boolean = false) => {
    if (isFetchingRef.current) return;
    const roles = Array.isArray((user as any)?.['https://bespot.com/roles']) ? (user as any)['https://bespot.com/roles'] : [];
    if (!isAuthenticated || !roles || roles.length === 0) {
      setEnterprise(false);
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      isFetchingRef.current = true;
      if (!silent) setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email read:organizations roles',
        },
      });

      // Use enterprise status from OrganizationsContext (already fetched in initial-data)
      // Only fetch separately if OrganizationsContext hasn't loaded yet (fallback)
      let isEnt = orgEnterprise;
      if (orgsLoading) {
        // Fallback: fetch enterprise if OrganizationsContext hasn't loaded yet
        try {
          const enterpriseRes = await axios.get<EnterpriseData>(`${process.env.REACT_APP_API_URL}/api/admin/user/enterprise`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          isEnt = !!enterpriseRes.data?.enterprise;
        } catch (e) {
          // If enterprise fetch fails, use orgEnterprise (may be false, but better than error)
          isEnt = orgEnterprise;
        }
      }
      setEnterprise(isEnt);

      // Only force refresh on subscription changes, not on initial load
      // This reduces Auth0 calls by using cached subscription data
      const refreshParam = isInitialLoadRef.current ? '' : '?refresh=true';
      const subscriptionRes = await axios.get<SubscriptionData>(
        `${process.env.REACT_APP_API_URL}/api/stripe/customer-subscription${refreshParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (isEnt) {
        setSubscription({ subscription: null, customer_id: null, tier: 'enterprise' });
      } else {
        setSubscription(subscriptionRes.data);
      }
    } catch (e: any) {
      // Handle refresh token errors gracefully
      if (e?.message?.includes('Missing Refresh Token')) {
        setError('Session expired. Please log in again.');
        setSubscription(null);
        setEnterprise(false);
        // Notify app shell to prompt logout/login
        dispatchRefreshTokenError();
      } else {
        setError(e?.message || 'Failed to load subscription');
        setSubscription(null);
      }
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, user, orgEnterprise, orgsLoading]);

  useEffect(() => {
    // Defer until roles present to avoid 401 console noise during activation
    const roles = Array.isArray((user as any)?.['https://bespot.com/roles']) ? (user as any)['https://bespot.com/roles'] : [];
    if (!isAuthenticated || !roles || roles.length === 0) return;
    
    // Also wait for OrganizationsContext to load enterprise status (to avoid duplicate request)
    // Increased timeout to 5 seconds to reduce unnecessary fallback calls
    // Batch endpoint typically loads in ~500ms, so 5s gives plenty of buffer
    const timeoutId = setTimeout(() => {
      if (orgsLoading) {
        // OrganizationsContext taking too long, proceed with fetch (will fallback to enterprise endpoint)
        fetchAll();
        isInitialLoadRef.current = false;
      }
    }, 5000);
    
    if (!orgsLoading) {
      // OrganizationsContext already loaded, use its enterprise status
      clearTimeout(timeoutId);
      fetchAll();
      isInitialLoadRef.current = false;
    }
    
    const onSubChanged = (e?: CustomEvent) => {
      // During subscription changes (especially reactivation), avoid flashing Free
      setLoading(true);
      isInitialLoadRef.current = false; // No longer initial load, force refresh
      // Add a small delay to ensure backend has processed the change
      setTimeout(() => fetchAll(true), 500);
      // In case of eventual consistency (Stripe/cache), re-fetch again shortly after
      setTimeout(() => fetchAll(true), 1500);
    };
    window.addEventListener('subscriptionChanged', onSubChanged as EventListener);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('subscriptionChanged', onSubChanged as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, orgsLoading, orgEnterprise]);

  // Optimistic update methods
  const optimisticUpdateSubscription = useCallback((updates: Partial<SubscriptionData>) => {
    setSubscription(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const optimisticCancelSubscription = useCallback(() => {
    setSubscription(prev => {
      if (!prev || !prev.subscription) return prev;
      return {
        ...prev,
        subscription: {
          ...prev.subscription,
          cancel_at_period_end: true
        }
      };
    });
  }, []);

  const optimisticReactivateSubscription = useCallback(() => {
    setSubscription(prev => {
      if (!prev || !prev.subscription) return prev;
      return {
        ...prev,
        subscription: {
          ...prev.subscription,
          cancel_at_period_end: false
        }
      };
    });
  }, []);

  const value = useMemo<SubscriptionContextValue>(() => ({
    enterprise,
    subscription,
    loading,
    error,
    refetch: fetchAll,
    optimisticUpdateSubscription,
    optimisticCancelSubscription,
    optimisticReactivateSubscription,
  }), [enterprise, subscription, loading, error, fetchAll, optimisticUpdateSubscription, optimisticCancelSubscription, optimisticReactivateSubscription]);

  // Keep enterprise flag in sync with global enterpriseChanged events (e.g. from EnterpriseToggle)
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const ce = e as CustomEvent<{ enterprise?: boolean }>;
        if (typeof ce.detail?.enterprise === 'boolean') {
          setEnterprise(ce.detail.enterprise);
        }
      } catch {
        // Best-effort only; ignore malformed events
      }
    };
    window.addEventListener('enterpriseChanged', handler as EventListener);
    return () => {
      window.removeEventListener('enterpriseChanged', handler as EventListener);
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = (): SubscriptionContextValue => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscriptionContext must be used within SubscriptionProvider');
  return ctx;
};


